//skip
import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormControl, Grid, TextField, Typography, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Autocomplete, InputAdornment } from "@mui/material";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";

import Box from '@mui/material/Box';
import { darkPalette } from "helpers/themeColors";
import Link from '@mui/material/Link';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CachedIcon from '@mui/icons-material/Cached';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import NoTransferIcon from '@mui/icons-material/NoTransfer';
import CircularProgress from '@mui/material/CircularProgress';

import RegularButton from "components/CustomButtons/Button";
import { SelectComponent } from "components/SelectMenu/SelectComponent";
import { SwitchCustom } from "components/SwitchCustom/SwitchCustom";

import {
  orderDetailsFieldValidations,
  orderDetailsInitialInputs,
} from "./inputValues";
import { getOrderCauses, editOrder, fetchServiceDetails, newPaymentLink } from "redux/actions/orders/ordersActions";
import { checkCoupon } from "redux/actions/customers/customersAction";
import { statusCombo, statusOrder } from "helpers/statusVariable";
import { formatCustomDate } from "helpers/dateTools";

import { useForm } from "hooks/useForm";
import { useSearch } from "hooks/useSearch";
import { PERMISSIONS } from "router/permissions";
import { useRuleValidator } from "hooks/useRuleValidator";
import { orderArrayV2 } from "helpers/orderArray";
import { makeDirection } from "helpers/makeDirection";
import { validateObject } from "helpers/validateObject";
import { convertToMapById } from "helpers/convertToMap";
import { cleanNumber } from "helpers/cleanNumbers";

import { applyCoupon, removeCoupon } from "redux/actions/coupons/couponsActions";
import { loadSuggestedUnits } from "redux/actions/unitsSection/unitsAction";
import { loadStationConfig } from "redux/actions/configSync/configSyncAction"
import { fetchStationUnits } from 'redux/actions/coveragesAssign/coveragesAssignActions'

import "../Customers/orders/SuggestedDateStyles.css";
import { CopyOrderDetailsButton } from "./CopyOrderDetailsButton";
import { SuggestionFeedbackModal } from "../Customers/orders/SuggestionFeedbackModal";
import { originLabelMap } from "helpers/dictionary";
import { findEtaForUnit, combineSuggestions } from "helpers/suggestionUtils";
import { PaymentTerminalBadge } from "components/PaymentTerminalBadge/PaymentTerminalBadge";
import { generateUUID } from "helpers/generateUUID";

const normalizeOrigin = (origin) => originLabelMap[origin] ? origin : "OTHER";

// Mapea el paymentType que envía el backend (español, con/sin acento) al value
// que usan las opciones de paymentMethods (inglés: CASH, CREDIT, etc.).
const PAYMENT_TYPE_MAP = {
  EFECTIVO: "CASH",
  CONTADO: "CASH",
  CREDITO: "CREDIT",
  "TARJETA DE CREDITO": "CREDIT_CARD",
  "TARJETA DE DEBITO": "DEBIT_CARD",
  "LIGA DE PAGO": "PAYMENT_LINK",
  TRANSFERENCIA: "TRANSFER",
};

const normalizePaymentType = (paymentType) => {
  if (!paymentType) return "";
  // Normaliza: mayúsculas y sin acentos
  const normalized = paymentType
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  // Si ya es un value en inglés, lo respeta; si viene en español, lo mapea
  return PAYMENT_TYPE_MAP[normalized] || normalized;
};

const DEFAULT_SUGGESTED_UNITS = 3;

export const OrderDetails = ({ navigation }) => {
  const { ORDERS } = PERMISSIONS;
  const { order, orderDetails, schedules, causes, generatedPaymentLink } = useSelector(({ orders }) => orders);
  const { setSnackbar } = useSearch();
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const canChangeStatus = useRuleValidator(PERMISSIONS.ORDERS_STATUS);
  const canSetDone = useRuleValidator(PERMISSIONS.ORDER_STATUS_DONE);
  const [unitsOpt, setUnitsOpt] = useState([]);
  const { units, suggestedUnits, suggestionFailed } = useSelector(
    ({ unitsReducer }) => unitsReducer || {},
  );
  const { stationConfig } = useSelector(({ configSync }) => configSync || {});
  const { appliedCoupon, paymentMethods, paymentStatus } = useSelector(({ customersReducer }) => customersReducer);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const { stationUnits } = useSelector((state) => state.coveragesAssign || {});
  const { brands } = useSelector(({ brandsReducer }) => brandsReducer || {});
  const cancelled = order?.status?.toLowerCase().includes("cancel");
  const hist = useHistory();
  const dispatch = useDispatch();
  const [suggestionStatus, setSuggestionStatus] = useState({ show: false });
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 2;
  const [allSuggestions, setAllSuggestions] = useState([]);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [pendingFormSubmission, setPendingFormSubmission] = useState(null);
  const [suggestedDate, setSuggestedDate] = useState(null);
  const [suggestedSchedule, setSuggestedSchedule] = useState(null);
  const [suggestedEtaMax, setSuggestedEtaMax] = useState(null);
  const processedSuggestionsRef = useRef(null);
  const [requestId] = useState(generateUUID());
  const [brandOptions, setBrandOptions] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const {
    customer,
    type,
    subtype,
    created,
    address,
    observations,
    total,
    eta,
  } = order;
  const addressComplete = makeDirection(address);
  const limitDays =
    schedules.length > 0
      ? {
          min: schedules.length > 0 && schedules[0].value,
          max: schedules.length > 0 && schedules[schedules.length - 1].value,
        }
      : {};
  const {
    inputValues,
    handleInputOnChange,
    fieldErrors,
    setFieldErrors,
    handleNewValues,
    checkboxOnChange,
  } = useForm(orderDetailsInitialInputs, orderDetailsFieldValidations);
  const { date, schedule, cause, unit, status, coupon, origin, discount, paymentType } = inputValues;
  const [timeOpt, setTimeOpt] = useState([]);
  const [isCancelled, setIsCancelled] = useState(false);
  const [orderCanceled, setOrderCanceled] = useState(false);
  const [lastValidatedCoupon, setLastValidatedCoupon] = useState('');

  const handleSchedule = ({ target }) => {
    setHasUserInteracted(true);
    handleNewValues({
      ...inputValues,
      date: target.value,
    });
    const scheduleFound = schedules.find((e) => e.value === target.value);
    if (scheduleFound) {
      setTimeOpt(scheduleFound.schedules);
    }
  };

  useEffect(() => {
    if (suggestionFailed) setSuggestionStatus({ show: true, status: 'error', text: 'Error al buscar sugerencias', icon: <CachedIcon color="error" /> })
  }, [suggestionFailed])

  useEffect(() => {
    if (suggestedUnits && suggestedUnits.error) {
      setSuggestionStatus({
        show: true,
        status: "error",
        text: "Error al buscar sugerencias",
        icon: <CachedIcon color="error" />,
      });
    }
  }, [suggestedUnits]);

  const handleRetry = () => {

    if (retryCount >= MAX_RETRIES && !suggestionStatus.showRetryOnly) return;

    if (!suggestionStatus.showRetryOnly) {
      setRetryCount((prev) => prev + 1);
    }

    makeSuggestionRequest();
  };

  const getErrorMessage = (definitiveReason) => {
    if (!definitiveReason) return null;

    const errorMessages = {
      "Incompatible Brand": "La marca/planta no puede servir esa ubicación.",
      "Insufficient Capacity": "No hay unidades con capacidad suficiente.",
      "Out of Polygon": "Dirección fuera del área de cobertura.",
      "Route with less than 3 points": "Ruta insuficiente para optimización.",
      "Unit without Polygon": "Unidad sin poligono asignado.",
      mobile_type_incompatibility: "Tipo de unidad móvil incompatible.",
      "no time available": "No hay ventanas de tiempo disponibles.",
      product_type_incompatible:
        "Producto incompatible con unidades disponibles.",
      product_type_incompatibility:
        "Producto incompatible con unidades disponibles.",
      stationary_type_incompatibility:
        "Tipo de unidad estacionaria incompatible.",
    };

    const firstKey = Object.keys(definitiveReason)[0];
    const errorCode = definitiveReason[firstKey];

    return errorMessages[errorCode] || errorCode;
  };

  const getSuggestionAlertConfig = () => {
    if (suggestionStatus.showRetryOnly) {
      return {
        text: "Generar sugerencias",
        backgroundColor: "rgb(244, 244, 244)",
        color: "#666",
        showRetryButton: true,
        buttonText: "BUSCAR SUGERENCIAS",
      };
    }
    if (retryCount >= MAX_RETRIES) {
      return {
        text: "Límite de intentos alcanzados",
        backgroundColor: "rgb(244, 244, 244)",
        color: "#666",
        icon: <CancelIcon style={{ fontSize: 16 }} />,
      };
    }
    if (suggestionStatus.status === "info") {
      return {
        text: "Generando sugerencias",
        backgroundColor: "rgb(244, 244, 244)",
        color: "#666",
        icon: <AutorenewIcon style={{ fontSize: 16 }} />,
      };
    }
    if (suggestionStatus.status === "success") {
      return {
        text: "Se encontraron coincidencias",
        backgroundColor: "rgb(186, 243, 196)",
        color: "rgb(37, 83, 32)",
        icon: <CheckIcon style={{ fontSize: 16 }} />,
      };
    }
    if (suggestionStatus.status === "warning") {
      const errorMessage = getErrorMessage(suggestionStatus.definitiveReason);
      return {
        text: errorMessage || "Sin coincidencias",
        backgroundColor: "#ffebee",
        color: "#d32f2f",
        icon: <CloseIcon style={{ fontSize: 16 }} />,
      };
    }
    if (suggestionStatus.status === "error") {
      return {
        text: "Sin coincidencias",
        backgroundColor: "#ffebee",
        color: "#d32f2f",
        icon: <CloseIcon style={{ fontSize: 16 }} />,
      };
    }
    return null;
  };

  const handleChange = (e, value) => {
    setHasUserInteracted(true);
    handleNewValues({
      ...inputValues,
      unit: value,
    });

    const matchedEta = findEtaForUnit(value, allSuggestions);
    setSuggestedEtaMax(matchedEta);
  };

  useEffect(() => {
    const toMap = convertToMapById(
      stationUnits.concat(Array.isArray(units) ? units : []).map((obj) => ({
        ...obj,
        label: `${obj.number} - ${obj.type} - ${obj.driver}`,
      })),
    );
    const sort = orderArrayV2(toMap, "label");

    let filteredUnits = sort.filter(
      (unit) =>
        unit.idStation === order?.idStation &&
        (type === "ESTACIONARIO"
          ? unit.type === "ESTACION" || unit.type === type
          : unit.type === type),
    );
    if (selectedBrand && selectedBrand.name !== "Todos") {
      filteredUnits = filteredUnits.filter(
        (unit) => unit.idBrand === selectedBrand.idBrand,
      );
    }

    const shouldClearUnit =
      unit && !filteredUnits.find((u) => u.id === unit.id);

    setUnitsOpt(filteredUnits);

    if (shouldClearUnit) {
      handleNewValues({
        ...inputValues,
        unit: null,
      });
    }
  }, [stationUnits, units, selectedBrand]);

  const [isProcessingSuggestions, setIsProcessingSuggestions] = useState(false);

  // Autocompleta la unidad del pedido en cuanto exista en unitsOpt.
  // No se ejecuta si el usuario ya interactuó, si ya hay unidad seleccionada,
  // o mientras se procesan sugerencias (para evitar pisar la asignación).
  useEffect(() => {
    if (hasUserInteracted || unit || isProcessingSuggestions) return;
    if (!order?.idUnit || !unitsOpt.length) return;

    const unitFind = unitsOpt.find(
      (un) => String(un.id) === String(order?.idUnit),
    );
    if (unitFind) {
      handleNewValues({
        ...inputValues,
        unit: unitFind,
      });
    }
  }, [
    unitsOpt,
    order?.idUnit,
    isProcessingSuggestions,
    unit,
    hasUserInteracted,
  ]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const isCancelled = status?.toLowerCase().includes("cancel");

    const combinedSuggestions = combineSuggestions(allSuggestions);

    const form = {
      cause: !isCancelled ? null : cause,
      date,
      status,
      unit: unit?.id,
      idSchedule: schedule,
      paymentType,
      origin: origin || normalizeOrigin(order?.serviceComplement?.origin),
      isAsap: !!inputValues.isAsap,
      metadata: {
        rapidCanvas: {
          ...combinedSuggestions,
          attempts: allSuggestions,
        },
      },
    };

    if (isCancelled && !cause)
      return setSnackbar("Seleccione una causa de cancelación");

    // Check if suggestions were provided and if selected unit was suggested
    const hasSuggestions = allSuggestions.length > 0;
    const hasValidUnitSuggestions = allSuggestions.some(
      (suggestion) =>
        suggestion.camion &&
        Object.values(suggestion.camion).some((value) => value !== null),
    );
    const selectedUnitWasSuggested = unit?.suggested;

    if (
      hasSuggestions &&
      hasValidUnitSuggestions &&
      !selectedUnitWasSuggested
    ) {
      // Show feedback modal if suggestions were provided but not used
      setPendingFormSubmission(form);
      setShowFeedbackModal(true);
    } else {
      // Submit directly if no suggestions or suggested unit was used
      dispatch(editOrder(order?.id, form, navigation));
    }
  };

  const handleFeedbackSubmit = (reason) => {
    if (pendingFormSubmission) {
      // Add feedback reason to metadata
      const formWithFeedback = {
        ...pendingFormSubmission,
        metadata: {
          ...pendingFormSubmission.metadata,
          rapidCanvas: {
            ...pendingFormSubmission.metadata.rapidCanvas,
            feedbackReason: reason,
          },
        },
      };

      dispatch(editOrder(order?.id, formWithFeedback, navigation));
      setPendingFormSubmission(null);
    }
  };

  const handleFeedbackClose = () => {
    setShowFeedbackModal(false);
    setPendingFormSubmission(null);
  };

  // const handleActiveCoupon = () => {
  //   const form = {
  //     id_service: order.id,
  //     id_coupon: _coupon,
  //     id_rule: _idRule,
  //     id_user_applies: id,
  //     id_customer: order.idCustomer,
  //   };
  //   dispatch(applyCoupon(form, setBlockCoupon, setCouponSelected, couponS));
  // };

  useEffect(() => {
    handleNewValues({
      ...inputValues,
      date: order?.date,
      schedule: order?.schedule?.id,
      status: order?.status,
      origin: normalizeOrigin(order?.serviceComplement?.origin),
      coupon: "",
      discount: 0,
    });
    setLastValidatedCoupon('');
    
    causes.length === 0 &&
      validateObject(order) &&
      dispatch(getOrderCauses(order?.idStation));
    cancelled && setIsCancelled(true);

    dispatch(loadStationConfig(order?.idStation, 'UNIT_SUGGESTION'))
    dispatch(fetchStationUnits(order?.idStation))
    dispatch(fetchServiceDetails(order?.id))
    
    return () => {
      dispatch({ type: 'SET_VALID_COUPON', payload: {} });
      setLastValidatedCoupon('');
    };
  }, [order?.id]);

  useEffect(() => {
    if (order?.date && order?.schedule?.id && order?.status) {
      handleNewValues({
        date: order.date,
        schedule: order.schedule.id,
        status: order.status,
      });
    }
  }, [order]);

  useEffect(() => {
    if ((orderDetails?.brand?.id || selectedBrand) && order?.idStation) {
      let brandId = orderDetails?.brand?.id || selectedBrand?.idBrand;
      
      // Si es "Todos", buscar la primera marca real
      if (selectedBrand?.name === 'Todos' && brandOptions.length > 1) {
        brandId = brandOptions[1].idBrand;
      }
      
      if (brandId) {
        dispatch(loadStationConfig(order.idStation, 'PAYMENT', brandId));
      }
    }
  }, [orderDetails, selectedBrand]);

  useEffect(() => {
    if (stationConfig?.OPENPAY) {
      setPaymentConfig(stationConfig.OPENPAY);
    } else {
      setPaymentConfig(null);
    }
  }, [stationConfig]);

  useEffect(() => {
    if (orderDetails?.coupon && total) {
      const couponData = orderDetails.coupon;
      const productData = orderDetails?.products?.[0];
      let initialDiscount = orderDetails?.discount || 0;
      
      if (couponData?.rule) {
        const totalNum = cleanNumber(total);
        const ruleDiscount = +couponData.rule.discount || 0;
        
        if (couponData.rule.unit === 'PORCENTAJE') {
          initialDiscount = (totalNum * ruleDiscount) / 100;
        } else if (couponData.rule.unit === 'IMPORTE') {
          initialDiscount = ruleDiscount;
        } else if ((couponData.rule.unit === 'LTS' || couponData.rule.unit === 'KGS') && productData) {
          const pricePerUnit = productData.price * (productData.product?.capacity || productData.capacity || 1);
          initialDiscount = ruleDiscount * (pricePerUnit || 0);
        }
      }
      
      const safeDiscount = isNaN(initialDiscount) ? 0 : +initialDiscount.toFixed(2);
      handleNewValues({
        ...inputValues,
        coupon: couponData?.code || "",
        discount: safeDiscount
      });
    }
  }, [orderDetails?.coupon?.id, orderDetails?.products, orderDetails?.discount, total]);

  useEffect(() => {
    const pt = orderDetails?.paymentType || order?.paymentType;
    if (pt) {
      handleNewValues({
        paymentType: normalizePaymentType(pt)
      });
    }
  }, [orderDetails?.paymentType, order?.paymentType]);

  // Única fuente de verdad para sincronizar el switch "Lo antes Posible"
  // con el valor que provee el backend (incluye la carga inicial).
  useEffect(() => {
    handleNewValues({ isAsap: !!order?.serviceComplement?.isAsap });
  }, [order?.serviceComplement?.isAsap]);

  useEffect(() => {
    if (brands && order?.idStation) {
      const uniqueBrands = Object.values(brands)
        .filter((brand) => brand.idStation === order?.idStation)
        .reduce((acc, brand) => {
          if (!acc.find((b) => b.idBrand === brand.idBrand)) {
            acc.push(brand);
          }
          return acc;
        }, [])
        .sort((a, b) => a.name.localeCompare(b.name));
      const options = [{ name: "Todos", id: "todos" }, ...uniqueBrands];
      setBrandOptions(options);
    }
  }, [brands, order?.idStation]);

  useEffect(() => {
    if (
      brandOptions.length > 0 &&
      !selectedBrand &&
      order?.idUnit &&
      stationUnits.length
    ) {
      const orderUnit = stationUnits.find((unit) => unit.id === order.idUnit);
      const orderBrandId = order?.serviceComplement?.brand?.id || orderUnit?.idBrand;
      const brand =
        brandOptions.find((b) => b.idBrand === orderBrandId || b.id === orderBrandId) ||
        brandOptions[0];
      setSelectedBrand(brand);
      setIsEditMode(true);
    }
  }, [brandOptions, order?.idUnit, stationUnits]);
  useEffect(() => {
    if (selectedBrand && hasUserInteracted) {
      setRetryCount(0);
      setAllSuggestions([]);
      setSuggestedDate(null);
      setSuggestedSchedule(null);
      setSuggestedEtaMax(null);
      setSuggestionStatus({ show: false });
    }
  }, [selectedBrand]);

  useEffect(() => {
    setSuggestionStatus({
      show: true,
      status: 'info',
      text: 'Generar sugerencias',
      showRetryOnly: true
    });
  }, []);

  useEffect(() => {
    dispatch({
      type: "SET_SUGGESTED_UNITS",
      payload: { units: null, responseTime: null },
    });

    setRetryCount(0);
    setAllSuggestions([]);
    setSuggestedDate(null);
    setSuggestedSchedule(null);
    setSuggestedEtaMax(null);
    setSuggestionStatus({
      show: true,
      status: "info",
      text: "Generar sugerencias",
      showRetryOnly: true,
    });
    processedSuggestionsRef.current = null;
    setHasUserInteracted(false);
  }, []);

  const makeSuggestionRequest = () => {
    let amount = null;
    if (subtype) {
      const parts = subtype.split(" ");
      amount = parts[0];

      if (isNaN(amount)) {
        const numberMatch = subtype.match(/\d+/);
        amount = numberMatch ? numberMatch[0] : null;
      }
    }

    if (
      !amount ||
      isNaN(amount) ||
      !address ||
      !type ||
      !date ||
      !schedule ||
      !Array.isArray(stationUnits) ||
      !stationUnits.length ||
      !Array.isArray(timeOpt) ||
      !timeOpt.length ||
      !stationConfig?.useRapidCanvas
    ) {
      if (!amount || isNaN(amount)) {
        setSuggestionStatus({
          show: true,
          status: "warning",
          text: "Cantidad no válida para sugerencias",
          icon: <CloseIcon color="warning" />,
        });
      }
      return;
    }

    if (!order?.id || !order?.idStation) {
      return;
    }

    const scheduleTime = timeOpt
      .find((obj) => obj.value === schedule)
      ?.label?.split("-")[0];
    if (!scheduleTime) {
      return;
    }

    if (!Array.isArray(stationUnits)) {
      return;
    }

    setSuggestionStatus({
      show: true,
      status: "info",
      text: "Generando sugerencias...",
      icon: <CircularProgress size="18px" color="info" />,
    });

    try {
      dispatch(
        loadSuggestedUnits(
          +amount,
          `${address.street} ${address.extNumber}, Colonia ${address.colony} ${address.postalCode} ${address.municipality} ${address.state}`,
          address.latitude,
          address.longitude,
          type,
          selectedBrand && selectedBrand?.name !== "Todos"
            ? selectedBrand.name
            : stationUnits.find((obj) => obj.brand)?.brand,
          date,
          scheduleTime,
          order?.idStation,
          null,
          requestId,
          stationConfig?.numSuggestions || DEFAULT_SUGGESTED_UNITS,
          order?.id,
          order?.idAccount || order.idAccount,
          order?.idCustomer || order.idCustomer,
          order?.phone || order.phone,
        ),
      );
    } catch (error) {
      setSuggestionStatus({
        show: true,
        status: "error",
        text: "Error al buscar sugerencias",
        icon: <CachedIcon color="error" />,
      });
    }
  };

  useEffect(() => {
    if (hasUserInteracted) {
      makeSuggestionRequest();
    }
  }, [selectedBrand]);

  useEffect(() => {
    if (hasUserInteracted) {
      makeSuggestionRequest();
    }
  }, [date]);

  useEffect(() => {
    if (hasUserInteracted) {
      makeSuggestionRequest();
    }
  }, [schedule]);

  useEffect(() => {
    if (!suggestedUnits.camion) return;
    if (!unitsOpt || unitsOpt.length === 0) return;

    // Don't process suggestions if they're from a different order
    if (suggestedUnits.requestBody) {
      try {
        const requestData = JSON.parse(suggestedUnits.requestBody);
        if (requestData.id_service && requestData.id_service !== order?.id) {
          return;
        }
      } catch (e) {
        // requestBody no es JSON válido: se ignora y se continúa el procesamiento
      }
    }

    const suggestionsKey = JSON.stringify(suggestedUnits);
    if (processedSuggestionsRef.current === suggestionsKey) return;
    processedSuggestionsRef.current = suggestionsKey;

    setIsProcessingSuggestions(true);

    setAllSuggestions((prev) => {
      const newSuggestion = {
        attempt: prev.length + 1,
        camion: suggestedUnits.camion,
        fecha: suggestedUnits.fecha,
        hora: suggestedUnits.hora,
        etaMax: suggestedUnits.etaMax || null,
        responseTime: suggestedUnits.responseTime || null,
        statusCode: suggestedUnits.statusCode || null,
        requestBody: suggestedUnits.requestBody || null,
        definitiveReason: suggestedUnits.definitiveReason || null,
      };
      return [...prev, newSuggestion];
    });

    const hasValidSuggestions = Object.values(suggestedUnits.camion).some(
      (value) => value !== null,
    );
    if (!hasValidSuggestions) {
      setSuggestionStatus({
        show: true,
        status: "warning",
        text: "Sin coincidencias",
        definitiveReason: suggestedUnits.definitiveReason,
        icon: <NoTransferIcon color="warning" />,
      });
      return;
    }

    let allCombinedUnits = [...Object.values(suggestedUnits.camion)];
    let allCombinedDates = [...Object.values(suggestedUnits.fecha)];
    let allCombinedTimes = [...Object.values(suggestedUnits.hora)];

    allSuggestions.forEach(suggestion => {
      allCombinedUnits = [...allCombinedUnits, ...Object.values(suggestion.camion)];
      allCombinedDates = [...allCombinedDates, ...Object.values(suggestion.fecha)];
      allCombinedTimes = [...allCombinedTimes, ...Object.values(suggestion.hora)];
    });

    let foundSuggestion = false
    const updatedUnits = unitsOpt.map(obj => {
      delete obj.suggested
      for (const suggestedNumber of allCombinedUnits) {
        if (suggestedNumber && (
          obj.number === suggestedNumber ||
          obj.number?.includes(suggestedNumber) ||
          suggestedNumber?.includes(obj.number) ||
          obj.number?.toLowerCase() === suggestedNumber?.toLowerCase()
        )) {
          obj.suggested = true
          foundSuggestion = true
        }
      }
      return obj
    }).sort((a, b) => !!b.suggested - !!a.suggested)

    setUnitsOpt(prevUnits => {
      return prevUnits.map(unit => {
        const updatedUnit = updatedUnits.find(u => u.id === unit.id);
        return updatedUnit || unit;
      });
    });
    setSuggestionStatus({ show: true, status: 'success', text: 'Se encontraron coincidencias', icon: <CheckIcon color="success" /> })

    const firstKey = Object.keys(suggestedUnits.camion)[0]
    if (suggestedUnits.fecha[firstKey]) {
      setSuggestedDate(suggestedUnits.fecha[firstKey]);
    }
    if (suggestedUnits.hora[firstKey]) {
      setSuggestedSchedule(suggestedUnits.hora[firstKey]);
    }
    if (suggestedUnits.etaMax?.[firstKey]) {
      setSuggestedEtaMax(suggestedUnits.etaMax[firstKey]);
    } else {
      setSuggestedEtaMax(null);
    }

    if (foundSuggestion) {
      const suggestedUnit = updatedUnits.find((obj) => obj.suggested);
      if (suggestedUnit) {
        handleNewValues({
          ...inputValues,
          unit: suggestedUnit,
        });
      }
    }

    setTimeout(() => {
      setIsProcessingSuggestions(false);
    }, 100);

  }, [suggestedUnits, unitsOpt])

  useEffect(() => {
    if (validateObject(order, true)) {
      hist.push("/admin/seguimiento_pedidos");
    } else if (schedules?.length > 0 && order?.date && order?.schedule?.id) {
      const orderSchedules = {
        label: order?.date,
        date: order?.date,
        value: order?.date,
        schedules: [
          {
            value: order?.schedule.id,
            label: `${order?.schedule.start} - ${order?.schedule.end}`,
          },
        ],
      };
      const newDate = formatCustomDate(new Date(), "-");

      if (newDate === order?.date) {
        const findSchedule = schedules?.find((e) => e.value === order?.date);
        setTimeOpt(findSchedule?.schedules || []);
      } else {
        const findSchedule = schedules?.find((e) => e.value === order?.date);
        if (validateObject(findSchedule, true)) {
          setTimeOpt(findSchedule?.schedules || []);
        } else {
          setTimeOpt(orderSchedules?.schedules || []);
        }
      }
      
      // Inicializar valores del formulario
      const unitFind = unitsOpt.find((un) => un.id === order?.idUnit);
      const updates = {
        ...inputValues,
        date: order?.date,
        schedule: order?.schedule.id,
        status: order?.status,
        unit: unitFind || inputValues.unit,
        paymentType: normalizePaymentType(
          orderDetails?.paymentType || order?.paymentType
        )
      };
      
      if (!orderDetails?.coupon?.id) {
        handleNewValues(updates);
      } else {
        handleNewValues({
          ...updates,
          coupon: inputValues.coupon || orderDetails.coupon.code,
          discount: inputValues.discount || orderDetails.discount || 0
        });
      }
    }
    setOrderCanceled(order?.status === statusOrder.CANCELED.status);
  }, [schedules, order]);

  useEffect(() => {
    if (status === statusOrder.CANCELED.status) {
      setIsCancelled(true);
    } else {
      setIsCancelled(false);
    }
  }, [status]);

  const validateCoupon = (e) => {
    const couponValue = e.target.value?.trim();
    if (!couponValue || couponValue === lastValidatedCoupon) return;
    setLastValidatedCoupon(couponValue);
    dispatch(checkCoupon({ 
      code: couponValue, 
      idCustomer: order?.idCustomer, 
      idAccount: order?.idAccount, 
      idProduct: order?.idProduct,
      idStation: order?.idStation,
      idBrand: orderDetails?.brand?.id
    }))
  }

  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleRemoveCoupon = () => {
    setShowConfirmModal(false);
    dispatch(removeCoupon(order?.id, orderDetails?.coupon?.id, () => {
      handleNewValues({ ...inputValues, coupon: "", discount: 0 });
      dispatch(fetchServiceDetails(order?.id));
    }));
  };

  const handleApplyCoupon = () => {
    if (!coupon || !appliedCoupon?.coupon?.id) {
      setSnackbar('Ingrese un cupón válido', 'warning');
      return;
    }
    
    const form = {
      id_service: order?.id,
      id_coupon: appliedCoupon.coupon.id
    };
    
    dispatch(applyCoupon(form, null, null, appliedCoupon.coupon));
    setTimeout(() => {
      dispatch(fetchServiceDetails(order?.id));
    }, 500);
  };

  useEffect(() => {
    if (orderDetails?.coupon?.id) return;
    
    if (!coupon) {
      handleNewValues({ ...inputValues, discount: 0 });
      setFieldErrors({ total: '' });
      return;
    }
    
    if (appliedCoupon?.coupon && appliedCoupon?.rule) {
      const totalNum = cleanNumber(total);
      
      if (totalNum < +appliedCoupon.rule.minimum) {
        handleNewValues({ ...inputValues, discount: 0 });
        return setFieldErrors({ total: 'Monto mínimo del cupón: $' + appliedCoupon.rule.minimum });
      }

      setFieldErrors({ total: '' });
      let appliedDiscount = 0;
      
      if (appliedCoupon.rule.unit === 'PORCENTAJE') {
        appliedDiscount = (totalNum * +appliedCoupon.rule.discount) / 100;
      } else if (appliedCoupon.rule.unit === 'IMPORTE') {
        appliedDiscount = +appliedCoupon.rule.discount;
      } else if ((appliedCoupon.rule.unit === 'LTS' || appliedCoupon.rule.unit === 'KGS') && orderDetails?.products?.[0]) {
        const pd = orderDetails.products[0];
        const discountQuantity = +appliedCoupon.rule.discount;
        const pricePerUnit = pd.price * (pd.product?.capacity || pd.capacity || 1);
        appliedDiscount = discountQuantity * (pricePerUnit || 0);
      }

      const safeDiscount = isNaN(appliedDiscount) ? 0 : +appliedDiscount.toFixed(2);
      handleNewValues({ ...inputValues, discount: safeDiscount });
    }
  }, [appliedCoupon, total, coupon, orderDetails?.coupon?.id])

  const generateOpenPaySessionId = () => {
    if (!paymentConfig) return null;
    window.OpenPay.setSandboxMode(process.env.REACT_APP_NODE_ENV != 'PROD')
    window.OpenPay.setId(paymentConfig.merchant_id ?? paymentConfig.API_KEY)
    window.OpenPay.setApiKey(paymentConfig.public_key ?? paymentConfig.API_KEY)
    return window.OpenPay.deviceData.setup()
  }

  const [isGeneratingPaymentLink, setIsGeneratingPaymentLink] = useState(false);

  const generatePaymentLink = () => {
    if (!paymentConfig || isGeneratingPaymentLink) return;
    
    setIsGeneratingPaymentLink(true);
    const totalNum = cleanNumber(total);
    const discountNum = +discount || 0;
    const amountNum = totalNum - discountNum;
    
    let sessionId = null;
    try {
      sessionId = generateOpenPaySessionId();
    } catch (error) {
      setSnackbar('Error al generar la sesión de pago', 'danger');
    }
    
    const paymentData = {
      id_customer: order?.idCustomer,
      amount: amountNum,
      description: `Pago de servicio ${orderDetails?.folio}`,
      currency: "MXN",
      origin: "SERVICES",
      id_order: order?.id,
      id_brand: orderDetails?.brand?.id,
      id_station: order?.idStation,
      type: "PAYMENT_LINK",
      processor: "OPENPAY",
      capture: false,
      device_session_id: sessionId,
      use_3d_secure: true
    }
    
    dispatch(newPaymentLink(paymentData))
  }

  useEffect(() => {
    if (generatedPaymentLink?.id) {
      setIsGeneratingPaymentLink(false);
      dispatch(fetchServiceDetails(order?.id));
    }
  }, [generatedPaymentLink]);

  const parsePaymentStatus = (status) => {
    return paymentStatus.find(obj => obj.value === status)?.label || 'DESCONOCIDO'
  }

  const parsePaymentType = (type, paymentLinkURL) => {
    if (paymentLinkURL) {
      return <Link href={paymentLinkURL} target="_blank">Liga de pago</Link>
    }
    return paymentMethods.find(obj => obj.value.toLowerCase() === type?.toLowerCase())?.label || 'DESCONOCIDO'
  }

  return (
    <FormControl fullWidth>
      <Grid container spacing={1}>
        <Grid
          item
          xs={12}
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 8,
          }}
        >
          <CopyOrderDetailsButton order={order} />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Cliente"
            name="customer"
            value={customer}
            variant="filled"
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Tipo"
            name="type"
            value={type}
            variant="filled"
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Total Surtido"
            name="subtype"
            value={subtype ? subtype : "Sin dato"}
            variant="filled"
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Fecha de Creación"
            name="created"
            value={created}
            variant="filled"
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            id="brand-autocomplete"
            options={brandOptions}
            value={selectedBrand}
            getOptionLabel={(brand) => brand.name || brand.alias}
            renderInput={(params) => (
              <TextField {...params} label="Marca" variant="outlined" />
            )}
            fullWidth
            onChange={(e, value) => {
              setHasUserInteracted(true);
              setSelectedBrand(value);
            }}
            disabled={orderCanceled || isEditMode}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DatePicker
            selected={date ? new Date(date + "T00:00:00") : null}
            onChange={(newDate) => {
              if (newDate) {
                const year = newDate.getFullYear();
                const month = String(newDate.getMonth() + 1).padStart(2, "0");
                const day = String(newDate.getDate()).padStart(2, "0");
                const formattedDate = `${year}-${month}-${day}`;
                handleSchedule({ target: { value: formattedDate } });
              }
            }}
            dateFormat="dd/MM/yyyy"
            minDate={limitDays.min ? new Date(limitDays.min) : undefined}
            maxDate={limitDays.max ? new Date(limitDays.max) : undefined}
            dayClassName={(date) => {
              const dateString = date.toISOString().split('T')[0];

              if (allSuggestions.length === 0) return '';

              const isSuggested = allSuggestions.some(suggestion =>
                Object.values(suggestion.fecha).includes(dateString)
              );

              return isSuggested ? 'suggested-date' : '';
            }}
            popperProps={{
              style: { zIndex: 9999 },
            }}
            customInput={
              <TextField
                label="Fecha de Surtido"
                variant="outlined"
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  startAdornment: suggestedDate &&
                    date &&
                    date === suggestedDate && (
                      <Box
                        width={8}
                        height={8}
                        bgcolor="#00e676"
                        borderRadius="50%"
                        mr={1}
                      />
                    ),
                  endAdornment: (
                    <Box style={{ cursor: "pointer", color: "#666" }}>📅</Box>
                  ),
                }}
                style={{ width: "100%" }}
              />
            }
            disabled={orderCanceled}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SelectComponent
            label="Horario de Entrega"
            name="schedule"
            options={timeOpt.map((timeOption) => {
              let isSuggested = false;
              if (suggestedSchedule) {
                const suggestedHour = parseInt(suggestedSchedule.split(":")[0]);
                const [startTime, endTime] = timeOption.label.split(" - ");
                const startHour = parseInt(startTime.split(":")[0]);
                const endHour = parseInt(endTime.split(":")[0]);
                isSuggested =
                  suggestedHour === startHour || suggestedHour === endHour;
              }
              return {
                ...timeOption,
                suggested: isSuggested,
              };
            })}
            value={schedule}
            onChange={(e) => {
              setHasUserInteracted(true);
              handleInputOnChange(e);
            }}
            error={fieldErrors.schedule}
            disabled={orderCanceled}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="ETA"
            name="eta"
            value={eta || "N/A"}
            variant="outlined"
            fullWidth
            disabled
            InputProps={{
              endAdornment: (
                <InputAdornment position="end"> min</InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={4} style={{ display: "flex", alignItems: "center" }}>
          <SwitchCustom
            name="isAsap"
            label="Lo antes Posible"
            checked={!!inputValues.isAsap}
            onChange={checkboxOnChange}
            disabled={orderCanceled}
          />
        </Grid>
        <Grid item xs={suggestedEtaMax ? 9 : 12}>
          <Autocomplete
            disabled={
              orderCanceled ||
              (suggestedUnits && stationConfig[ORDERS]?.forceUnit)
            }
            id="order-unit-autocomplete"
            options={orderArrayV2(unitsOpt)}
            value={unit}
            getOptionLabel={(unit) => unit.label}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            renderOption={({ key, ...props }, option) => (
              <Box
                component="li"
                key={option.id ?? key}
                {...props}
                display="flex"
                alignItems="center"
                gap={1}
              >
                {option.suggested && (
                  <Box
                    width={8}
                    height={8}
                    bgcolor="#00e676"
                    borderRadius="50%"
                  />
                )}
                <span>{option.label}</span>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Unidad"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (() => {
                    const isCurrentUnitSuggested = unit && allSuggestions.some(suggestion =>
                      Object.values(suggestion.camion).some(suggestedNumber =>
                        suggestedNumber && (
                          unit.number === suggestedNumber ||
                          unit.number?.includes(suggestedNumber) ||
                          suggestedNumber?.includes(unit.number) ||
                          unit.number?.toLowerCase() === suggestedNumber?.toLowerCase()
                        )
                      )
                    );

                    return isCurrentUnitSuggested ? (
                      <Box
                        width={8}
                        height={8}
                        bgcolor="#00e676"
                        borderRadius="50%"
                        mr={1}
                      />
                    ) : null;
                  })(),
                  endAdornment: params.InputProps.endAdornment,
                }}
              />
            )}
            fullWidth
            onChange={(e, v) => handleChange(e, v)}
          />
        </Grid>
        {suggestedEtaMax && (
          <Grid item xs={3}>
            <TextField
              label="ETA"
              value={String(suggestedEtaMax)}
              variant="outlined"
              fullWidth
              disabled
              InputProps={{
                style: { fontWeight: 500 },
              }}
            />
          </Grid>
        )}
        {unit && (
          <PaymentTerminalBadge paymentTerminal={unit.hasPaymentTerminal} />
        )}

        {suggestionStatus.show && !suggestionStatus.showRetryOnly && (
          <Grid item xs={12}>
            <Typography
              variant="body2"
              style={{
                color: "#666",
                fontStyle: "normal",
                textAlign: "center",
              }}
            >
              Debes seleccionar una nueva asignación de forma manual
            </Typography>
          </Grid>
        )}
        {suggestionStatus.show && (
          <Grid item xs={12}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              {!suggestionStatus.showRetryOnly && (
                <Box
                  px={2}
                  py={1}
                  borderRadius={1}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  style={{
                    backgroundColor:
                      getSuggestionAlertConfig()?.backgroundColor ||
                      "rgb(244, 244, 244)",
                    color: getSuggestionAlertConfig()?.color || "#666",
                  }}
                >
                  {getSuggestionAlertConfig()?.icon}
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {getSuggestionAlertConfig()?.text}
                  </Typography>
                </Box>
              )}

              <Box
                onClick={handleRetry}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  cursor: "pointer",
                  color: "rgb(58, 134, 238)",
                  textDecoration: "underline",
                  fontSize: "14px",
                  fontWeight: 500,
                  ...(suggestionStatus.showRetryOnly
                    ? { marginLeft: "auto" }
                    : {}),
                }}
              >
                {suggestionStatus.showRetryOnly ? "SUGERENCIAS" : "REINTENTAR"}
                <RefreshIcon
                  style={{ fontSize: 16, color: "rgb(58, 134, 238)" }}
                />
              </Box>
            </Box>
          </Grid>
        )}
        <Grid item xs={12}>
          <SelectComponent
            label="Estatus"
            name="status"
            options={statusCombo}
            value={status}
            onChange={(e) => {
              if (e.target.value === statusOrder.DONE.status && !canSetDone) {
                setSnackbar('No tienes permiso para cambiar el estatus a Surtido', 'warning');
                return;
              }
              handleInputOnChange(e);
            }}
            error={fieldErrors.status}
            fullWidth
            disabled={orderCanceled || !canChangeStatus}
          />
        </Grid>
        {isCancelled && order?.status !== statusOrder.CANCELED.status && (
          <Grid
            item
            xs={12}
            style={{
              transition: "all .3s",
            }}
          >
            <SelectComponent
              label="Causa de la cancelación"
              name="cause"
              options={causes}
              value={cause}
              onChange={handleInputOnChange}
              error={fieldErrors.cause}
              fullWidth
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <TextField
            label="Dirección"
            name="addressComplete"
            value={addressComplete}
            variant="filled"
            fullWidth
            multiline
          />
        </Grid>
        <Grid item xs={12}>
          <Box display="flex" gap={1}>
            <TextField
              label="Cupón"
              name="coupon"
              value={coupon}
              onChange={handleInputOnChange}
              onBlur={validateCoupon}
              variant="outlined"
              error={fieldErrors.coupon}
              helperText={fieldErrors.coupon || ""}
              disabled={!!orderDetails?.coupon?.id}
              InputProps={{
                endAdornment: orderDetails?.coupon?.id && (
                  <IconButton
                    size="small"
                    onClick={() => setShowConfirmModal(true)}
                    edge="end"
                  >
                    <CloseIcon />
                  </IconButton>
                )
              }}
            />
            {!orderDetails?.coupon?.id && coupon && appliedCoupon?.coupon && (
              <RegularButton
                color="success"
                onClick={handleApplyCoupon}
                style={{ minWidth: '120px' }}
              >
                Aplicar
              </RegularButton>
            )}
          </Box>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Descuento"
            value={discount}
            variant="outlined"
            InputProps={{
              inputProps: {
                min: 0,
                right: "1",
              },
            }}
            disabled
          />
        </Grid>
        {orderDetails?.products?.[0]?.quantity === 0 && (
          <Grid item xs={12}>
            <Box
              px={2}
              py={1}
              borderRadius={1}
              display="flex"
              alignItems="center"
              gap={1}
              sx={{
                bgcolor: (theme) => (theme.palette.mode === "dark" ? darkPalette.selectedBg : "#e3f2fd"),
                color: (theme) => (theme.palette.mode === "dark" ? darkPalette.accent : "#1976d2"),
              }}
            >
              <Typography variant="body2" style={{ fontWeight: 500 }}>
                ⛽ Tanque lleno
              </Typography>
            </Box>
          </Grid>
        )}
        <Grid item xs={12}>
          <TextField
            label="Monto"
            name="total"
            value={total}
            variant="filled"
            fullWidth
            disabled
          />
        </Grid>
        <Grid item xs={12}>
          <SelectComponent
            label="Tipo de pago"
            name="paymentType"
            value={paymentType}
            onChange={handleInputOnChange}
            options={paymentMethods}
            fullWidth
            disabled={orderCanceled}
          />
        </Grid>
        {orderDetails?.payments?.map((payment, index) => {
          return <React.Fragment key={index}>
            <Grid item xs={4}>
              {`${payment.amount} ${payment.currency?.toUpperCase()}`}
            </Grid>
            <Grid item xs={4}>
              {parsePaymentType(payment.type, payment.payment_url || payment.paymentUrl)}
            </Grid>
            <Grid item xs={4}>
              {parsePaymentStatus(payment.status)}
            </Grid>
          </React.Fragment>
        })}
        {orderDetails.paymentType?.toLowerCase() === 'payment_link' && !orderDetails.payments?.length &&
          <Grid item xs={12}>
            <RegularButton
              color="success"
              tooltip={!paymentConfig ? "Configuración de pago no disponible" : "Generar liga de pago"}
              onClick={generatePaymentLink}
              fullWidth
              disabled={!paymentConfig || orderDetails.payments?.length || isGeneratingPaymentLink}
            >
              Generar liga de pago
            </RegularButton>
          </Grid>
        }
        <Grid item xs={12}>
          <TextField
            label="Observaciones"
            name="observations"
            value={observations}
            variant="filled"
            fullWidth
            multiline
          />
        </Grid>
        <Grid item xs={12} style={{}}>
          <RegularButton
            color="info"
            tooltip="Aceptar"
            onClick={handleSubmit}
            fullWidth
            disabled={cancelled}
          >
            Guardar
          </RegularButton>
        </Grid>
      </Grid>

      <SuggestionFeedbackModal
        open={showFeedbackModal}
        onClose={handleFeedbackClose}
        onSubmit={handleFeedbackSubmit}
      />

      <Dialog open={showConfirmModal} onClose={() => setShowConfirmModal(false)}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro que deseas eliminar el cupón aplicado?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmModal(false)} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleRemoveCoupon} color="secondary">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </FormControl>
  );
};
